/**
 * PERMANENT procedural fallback: any asset id missing from the atlases is drawn here with
 * Canvas 2D at first use, so the game always runs (no art build, partial sheets, offline CDN).
 * Output sizes match the art pipeline's nominal sizes so layout code never special-cases.
 */
import { itemDef, itemsForShop } from '../config/content.js';
import { C, FONT, ITEM_COLORS, css } from './theme.js';

type Ctx = CanvasRenderingContext2D;
type Draw = (ctx: Ctx, w: number, h: number) => void;

export interface FallbackSpec {
  w: number;
  h: number;
  draw: Draw;
}

const INK = css(C.ink);

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

function rr(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  const k = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + k, y);
  ctx.arcTo(x + w, y, x + w, y + h, k);
  ctx.arcTo(x + w, y + h, x, y + h, k);
  ctx.arcTo(x, y + h, x, y, k);
  ctx.arcTo(x, y, x + w, y, k);
  ctx.closePath();
}

function starPath(ctx: Ctx, cx: number, cy: number, ro: number, ri: number, n = 5, rot = -Math.PI / 2): void {
  ctx.beginPath();
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 ? ri : ro;
    const a = rot + (i * Math.PI) / n;
    ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  ctx.closePath();
}

function polyPath(ctx: Ctx, cx: number, cy: number, r: number, n: number, rot = -Math.PI / 2): void {
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = rot + (i * 2 * Math.PI) / n;
    ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  ctx.closePath();
}

function heartPath(ctx: Ctx, cx: number, cy: number, s: number): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy + s * 0.9);
  ctx.bezierCurveTo(cx - s * 1.5, cy - s * 0.1, cx - s * 0.8, cy - s * 1.2, cx, cy - s * 0.45);
  ctx.bezierCurveTo(cx + s * 0.8, cy - s * 1.2, cx + s * 1.5, cy - s * 0.1, cx, cy + s * 0.9);
  ctx.closePath();
}

function shade(hex: number, f: number): string {
  const r = (hex >> 16) & 255;
  const g = (hex >> 8) & 255;
  const b = hex & 255;
  const m = (v: number) => Math.max(0, Math.min(255, Math.round(f >= 0 ? v + (255 - v) * f : v * (1 + f))));
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}

function fillStroke(ctx: Ctx, fill: string, lw: number, stroke = INK): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = lw;
  ctx.strokeStyle = stroke;
  ctx.lineJoin = 'round';
  ctx.stroke();
}

function label(ctx: Ctx, text: string, x: number, y: number, size: number, color = '#fff', stroke: string | null = INK): void {
  ctx.font = `700 ${size}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (stroke) {
    ctx.lineWidth = size * 0.18;
    ctx.strokeStyle = stroke;
    ctx.strokeText(text, x, y);
  }
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

function gloss(ctx: Ctx, cx: number, cy: number, rx: number, ry: number): void {
  ctx.save();
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, -0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------- items

const SHAPES = ['circle', 'square', 'triangle', 'star', 'heart', 'diamond', 'hex', 'drop', 'flower', 'cloud', 'ring'] as const;

function shapePath(ctx: Ctx, shape: (typeof SHAPES)[number], cx: number, cy: number, r: number): void {
  switch (shape) {
    case 'circle':
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      return;
    case 'square':
      rr(ctx, cx - r * 0.9, cy - r * 0.9, r * 1.8, r * 1.8, r * 0.35);
      return;
    case 'triangle': {
      ctx.beginPath();
      const pts = [0, 1, 2].map((i) => [cx + Math.cos(-Math.PI / 2 + (i * 2 * Math.PI) / 3) * r * 1.12, cy + 0.12 * r + Math.sin(-Math.PI / 2 + (i * 2 * Math.PI) / 3) * r * 1.12]);
      ctx.moveTo((pts[0]![0]! + pts[2]![0]!) / 2, (pts[0]![1]! + pts[2]![1]!) / 2);
      for (let i = 0; i < 3; i++) {
        const p = pts[i]!;
        const q = pts[(i + 1) % 3]!;
        ctx.arcTo(p[0]!, p[1]!, (p[0]! + q[0]!) / 2, (p[1]! + q[1]!) / 2, r * 0.28);
      }
      ctx.closePath();
      return;
    }
    case 'star':
      starPath(ctx, cx, cy + r * 0.05, r * 1.08, r * 0.55);
      return;
    case 'heart':
      heartPath(ctx, cx, cy + r * 0.05, r * 0.95);
      return;
    case 'diamond':
      polyPath(ctx, cx, cy, r * 1.05, 4);
      return;
    case 'hex':
      polyPath(ctx, cx, cy, r, 6, 0);
      return;
    case 'drop':
      ctx.beginPath();
      ctx.moveTo(cx, cy - r * 1.1);
      ctx.bezierCurveTo(cx + r * 0.3, cy - r * 0.5, cx + r * 0.95, cy, cx + r * 0.9, cy + r * 0.35);
      ctx.arc(cx, cy + r * 0.3, r * 0.9, 0, Math.PI);
      ctx.bezierCurveTo(cx - r * 0.95, cy, cx - r * 0.3, cy - r * 0.5, cx, cy - r * 1.1);
      ctx.closePath();
      return;
    case 'flower': {
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
        ctx.moveTo(cx + Math.cos(a) * r * 0.55 + r * 0.48, cy + Math.sin(a) * r * 0.55);
        ctx.arc(cx + Math.cos(a) * r * 0.55, cy + Math.sin(a) * r * 0.55, r * 0.48, 0, Math.PI * 2);
      }
      return;
    }
    case 'cloud':
      ctx.beginPath();
      ctx.arc(cx - r * 0.45, cy + r * 0.15, r * 0.55, Math.PI * 0.5, Math.PI * 1.5);
      ctx.arc(cx, cy - r * 0.25, r * 0.62, Math.PI, Math.PI * 2);
      ctx.arc(cx + r * 0.5, cy + r * 0.15, r * 0.5, Math.PI * 1.5, Math.PI * 0.5);
      ctx.closePath();
      return;
    case 'ring':
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.moveTo(cx + r * 0.42, cy);
      ctx.arc(cx, cy, r * 0.42, 0, Math.PI * 2, true);
      return;
  }
}

function itemInitials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length > 1) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return name.slice(0, 2).replace(/^./, (c) => c.toUpperCase());
}

function drawItem(id: string, cb: boolean): Draw {
  const def = itemDef(id);
  const list = def ? itemsForShop(def.shop) : [];
  const idx = Math.max(0, list.findIndex((x) => x.id === id));
  const k = def ? idx : hash(id);
  const shape = SHAPES[k % SHAPES.length]!;
  const color = ITEM_COLORS[(k * 5) % ITEM_COLORS.length]!;
  const text = itemInitials(def?.name ?? id.split('_').pop() ?? '?');
  return (ctx, w, h) => {
    const cx = w / 2;
    const cy = h / 2;
    const r = w * 0.36;
    ctx.save();
    ctx.translate(0, w * 0.03);
    ctx.globalAlpha = 0.25;
    shapePath(ctx, shape, cx, cy + w * 0.02, r);
    ctx.fillStyle = '#000';
    ctx.fill('evenodd');
    ctx.restore();
    shapePath(ctx, shape, cx, cy, r);
    const g = ctx.createLinearGradient(0, cy - r, 0, cy + r);
    g.addColorStop(0, shade(color, 0.25));
    g.addColorStop(1, shade(color, -0.12));
    ctx.fillStyle = g;
    ctx.fill('evenodd');
    if (cb) {
      ctx.save();
      shapePath(ctx, shape, cx, cy, r);
      ctx.clip('evenodd');
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = w * 0.035;
      const mode = k % 3;
      for (let i = -w; i < w * 2; i += w * 0.12) {
        ctx.beginPath();
        if (mode === 0) {
          ctx.moveTo(i, 0);
          ctx.lineTo(i + h, h);
        } else if (mode === 1) {
          ctx.moveTo(i, 0);
          ctx.lineTo(i, h);
        } else {
          ctx.arc(cx, cy, Math.abs(i) * 0.3 + 4, 0, Math.PI * 2);
        }
        ctx.stroke();
      }
      ctx.restore();
    }
    shapePath(ctx, shape, cx, cy, r);
    ctx.lineWidth = w * 0.045;
    ctx.strokeStyle = INK;
    ctx.lineJoin = 'round';
    ctx.stroke();
    gloss(ctx, cx - r * 0.35, cy - r * 0.45, r * 0.28, r * 0.14);
    label(ctx, text, cx, cy + r * 0.06, w * (cb ? 0.26 : 0.22), '#fff', INK);
  };
}

// ---------------------------------------------------------------- Pip

interface Face {
  eyes: 'open' | 'blink' | 'happy' | 'wide' | 'worried' | 'closed' | 'droopy';
  mouth: 'smile' | 'o' | 'grin' | 'wavy' | 'scream' | 'frown' | 'flat';
  sy: number;
  dx: number;
  dy: number;
  sweat?: boolean;
  sparkle?: boolean;
  zzz?: boolean;
  tilt?: number;
}

const PIP_FRAMES: Record<string, Face[]> = {
  idle: [
    { eyes: 'open', mouth: 'smile', sy: 1, dx: 0, dy: 0 },
    { eyes: 'open', mouth: 'smile', sy: 0.98, dx: 0, dy: 2 },
    { eyes: 'open', mouth: 'smile', sy: 0.97, dx: 0, dy: 3 },
    { eyes: 'blink', mouth: 'smile', sy: 0.98, dx: 0, dy: 2 },
  ],
  catch: [
    { eyes: 'wide', mouth: 'o', sy: 1.06, dx: 0, dy: -6 },
    { eyes: 'open', mouth: 'o', sy: 0.94, dx: 0, dy: 4 },
    { eyes: 'happy', mouth: 'smile', sy: 1, dx: 0, dy: 0 },
  ],
  pop: [
    { eyes: 'happy', mouth: 'grin', sy: 1.08, dx: 0, dy: -12 },
    { eyes: 'happy', mouth: 'grin', sy: 1.02, dx: 0, dy: -6 },
    { eyes: 'happy', mouth: 'grin', sy: 0.95, dx: 0, dy: 3, sparkle: true },
    { eyes: 'open', mouth: 'smile', sy: 1, dx: 0, dy: 0 },
  ],
  worried: [
    { eyes: 'worried', mouth: 'wavy', sy: 0.98, dx: -2, dy: 1, sweat: true },
    { eyes: 'worried', mouth: 'wavy', sy: 0.98, dx: 2, dy: 1, sweat: true },
  ],
  panic: [
    { eyes: 'wide', mouth: 'scream', sy: 1.05, dx: -6, dy: -2, sweat: true },
    { eyes: 'wide', mouth: 'scream', sy: 1.05, dx: 6, dy: -2, sweat: true },
    { eyes: 'wide', mouth: 'scream', sy: 1.02, dx: -4, dy: 0, sweat: true },
    { eyes: 'wide', mouth: 'scream', sy: 1.02, dx: 4, dy: 0, sweat: true },
  ],
  win: [
    { eyes: 'happy', mouth: 'grin', sy: 1.1, dx: 0, dy: -18, sparkle: true },
    { eyes: 'happy', mouth: 'grin', sy: 1.04, dx: 0, dy: -8, sparkle: true },
    { eyes: 'happy', mouth: 'grin', sy: 0.94, dx: 0, dy: 4 },
    { eyes: 'happy', mouth: 'grin', sy: 1, dx: 0, dy: 0, sparkle: true },
  ],
  celebrate: [
    { eyes: 'happy', mouth: 'grin', sy: 1.1, dx: 0, dy: -18, sparkle: true, tilt: -0.12 },
    { eyes: 'happy', mouth: 'grin', sy: 1, dx: 0, dy: -4, tilt: 0.12 },
    { eyes: 'happy', mouth: 'grin', sy: 0.95, dx: 0, dy: 4, sparkle: true },
  ],
  slump: [
    { eyes: 'droopy', mouth: 'frown', sy: 0.92, dx: 0, dy: 8 },
    { eyes: 'closed', mouth: 'frown', sy: 0.9, dx: 0, dy: 10 },
  ],
  wave: [
    { eyes: 'happy', mouth: 'smile', sy: 1, dx: 0, dy: 0, tilt: -0.08 },
    { eyes: 'happy', mouth: 'smile', sy: 1, dx: 0, dy: 0, tilt: 0.08 },
  ],
  sleep: [
    { eyes: 'closed', mouth: 'flat', sy: 0.97, dx: 0, dy: 3, zzz: true },
    { eyes: 'closed', mouth: 'flat', sy: 1, dx: 0, dy: 0, zzz: true },
  ],
};

export const PIP_ACTIONS = Object.keys(PIP_FRAMES);

export function pipFrameCount(action: string): number {
  return PIP_FRAMES[action]?.length ?? 1;
}

function drawPip(face: Face): Draw {
  return (ctx, w, h) => {
    const s = w / 256;
    ctx.save();
    ctx.translate(w / 2 + face.dx * s, h * 0.93 + face.dy * s);
    ctx.rotate(face.tilt ?? 0);
    ctx.scale(1 / Math.sqrt(face.sy), face.sy);
    // shadow
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.ellipse(0, 2 * s, 84 * s, 12 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    // handles
    ctx.lineWidth = 11 * s;
    ctx.strokeStyle = '#8a5a36';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-46 * s, -168 * s);
    ctx.bezierCurveTo(-46 * s, -222 * s, -8 * s, -222 * s, -10 * s, -172 * s);
    ctx.moveTo(10 * s, -172 * s);
    ctx.bezierCurveTo(8 * s, -222 * s, 46 * s, -222 * s, 46 * s, -168 * s);
    ctx.stroke();
    // body
    ctx.beginPath();
    ctx.moveTo(-78 * s, -168 * s);
    ctx.quadraticCurveTo(0, -182 * s, 78 * s, -168 * s);
    ctx.lineTo(88 * s, -18 * s);
    ctx.quadraticCurveTo(88 * s, 0, 70 * s, 0);
    ctx.lineTo(-70 * s, 0);
    ctx.quadraticCurveTo(-88 * s, 0, -88 * s, -18 * s);
    ctx.closePath();
    const g = ctx.createLinearGradient(0, -180 * s, 0, 0);
    g.addColorStop(0, '#f2c58f');
    g.addColorStop(1, '#dea468');
    fillStroke(ctx, '#000', 7 * s);
    ctx.fillStyle = g;
    ctx.fill();
    ctx.stroke();
    // rim
    ctx.beginPath();
    ctx.moveTo(-78 * s, -168 * s);
    ctx.quadraticCurveTo(0, -182 * s, 78 * s, -168 * s);
    ctx.lineTo(80 * s, -146 * s);
    ctx.quadraticCurveTo(0, -158 * s, -80 * s, -146 * s);
    ctx.closePath();
    ctx.fillStyle = '#cf9152';
    ctx.fill();
    // face
    const ey = -92 * s;
    const ex = 30 * s;
    ctx.fillStyle = '#2b1a14';
    ctx.strokeStyle = '#2b1a14';
    ctx.lineWidth = 7 * s;
    ctx.lineCap = 'round';
    for (const side of [-1, 1]) {
      const x = side * ex;
      ctx.beginPath();
      switch (face.eyes) {
        case 'open':
          ctx.ellipse(x, ey, 12 * s, 16 * s, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.beginPath();
          ctx.arc(x + 4 * s, ey - 6 * s, 4.5 * s, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#2b1a14';
          break;
        case 'wide':
          ctx.fillStyle = '#fff';
          ctx.ellipse(x, ey, 17 * s, 20 * s, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = '#2b1a14';
          ctx.beginPath();
          ctx.arc(x, ey + 2 * s, 7 * s, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 'happy':
          ctx.arc(x, ey + 6 * s, 12 * s, Math.PI * 1.1, Math.PI * 1.9);
          ctx.stroke();
          break;
        case 'blink':
        case 'closed':
          ctx.moveTo(x - 12 * s, ey + 2 * s);
          ctx.quadraticCurveTo(x, ey + 10 * s, x + 12 * s, ey + 2 * s);
          ctx.stroke();
          break;
        case 'worried':
          ctx.ellipse(x, ey + 2 * s, 11 * s, 14 * s, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(x - 14 * s * side * -1, ey - 26 * s);
          ctx.lineTo(x + 12 * s * side * -1, ey - 18 * s);
          ctx.stroke();
          break;
        case 'droopy':
          ctx.ellipse(x, ey + 6 * s, 11 * s, 8 * s, 0, 0, Math.PI);
          ctx.fill();
          break;
      }
    }
    // blush
    ctx.fillStyle = 'rgba(255,120,110,0.55)';
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(side * 54 * s, -62 * s, 13 * s, 7 * s, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // mouth
    const my = -58 * s;
    ctx.strokeStyle = '#2b1a14';
    ctx.fillStyle = '#7a2a2a';
    ctx.lineWidth = 6 * s;
    ctx.beginPath();
    switch (face.mouth) {
      case 'smile':
        ctx.arc(0, my - 8 * s, 16 * s, Math.PI * 0.2, Math.PI * 0.8);
        ctx.stroke();
        break;
      case 'grin':
        ctx.moveTo(-22 * s, my - 6 * s);
        ctx.quadraticCurveTo(0, my + 26 * s, 22 * s, my - 6 * s);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        break;
      case 'o':
        ctx.ellipse(0, my, 9 * s, 11 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        break;
      case 'scream':
        ctx.ellipse(0, my + 4 * s, 15 * s, 20 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        break;
      case 'wavy':
        ctx.moveTo(-16 * s, my);
        ctx.quadraticCurveTo(-8 * s, my - 8 * s, 0, my);
        ctx.quadraticCurveTo(8 * s, my + 8 * s, 16 * s, my);
        ctx.stroke();
        break;
      case 'frown':
        ctx.arc(0, my + 14 * s, 14 * s, Math.PI * 1.2, Math.PI * 1.8);
        ctx.stroke();
        break;
      case 'flat':
        ctx.moveTo(-10 * s, my);
        ctx.lineTo(10 * s, my);
        ctx.stroke();
        break;
    }
    if (face.sweat) {
      ctx.fillStyle = '#7fd3ff';
      ctx.strokeStyle = INK;
      ctx.lineWidth = 3 * s;
      ctx.beginPath();
      ctx.moveTo(72 * s, -140 * s);
      ctx.quadraticCurveTo(86 * s, -118 * s, 72 * s, -112 * s);
      ctx.quadraticCurveTo(58 * s, -118 * s, 72 * s, -140 * s);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
    if (face.sparkle) {
      ctx.fillStyle = '#fff3a0';
      ctx.strokeStyle = INK;
      ctx.lineWidth = 3 * s;
      for (const [x, y, r] of [
        [40, 60, 16],
        [214, 44, 12],
        [226, 120, 9],
      ] as const) {
        starPath(ctx, x * s, y * s, r * s, r * 0.4 * s, 4);
        ctx.fill();
        ctx.stroke();
      }
    }
    if (face.zzz) label(ctx, 'z', w * 0.8, h * 0.15, 36 * s, '#fff', INK);
  };
}

function drawBagBody(): Draw {
  return (ctx, w, h) => {
    const r = h * 0.28;
    ctx.save();
    ctx.globalAlpha = 0.2;
    rr(ctx, w * 0.02, h * 0.1, w * 0.96, h * 0.88, r);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.restore();
    rr(ctx, w * 0.02, h * 0.04, w * 0.96, h * 0.86, r);
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#f4cc98');
    g.addColorStop(1, '#d99a58');
    ctx.fillStyle = g;
    ctx.fill();
    ctx.lineWidth = h * 0.045;
    ctx.strokeStyle = INK;
    ctx.stroke();
    rr(ctx, w * 0.05, h * 0.16, w * 0.9, h * 0.62, r * 0.8);
    ctx.fillStyle = 'rgba(90,46,31,0.22)';
    ctx.fill();
  };
}

// ---------------------------------------------------------------- customers

const SKIN = ['#f9d4b4', '#f1c27d', '#e0ac69', '#c68642', '#8d5524', '#ffdbac'];
const HAIR = ['#2b1a14', '#6b3e26', '#d9a441', '#a33b20', '#555', '#f2e2c4', '#3b3b6d'];
const SHIRT = [0x3a86ff, 0xff6b5b, 0x2ec4b6, 0x8338ec, 0xffc845, 0x3bb273, 0xff70a6];

function drawCustomer(id: string, pose: string): Draw {
  const k = hash(id);
  const skin = SKIN[k % SKIN.length]!;
  const hair = HAIR[(k >>> 3) % HAIR.length]!;
  const shirt = SHIRT[(k >>> 6) % SHIRT.length]!;
  const style = (k >>> 9) % 3;
  return (ctx, w, h) => {
    const s = w / 256;
    const cx = w / 2;
    // shoulders
    ctx.beginPath();
    ctx.moveTo(cx - 100 * s, h);
    ctx.quadraticCurveTo(cx - 96 * s, 176 * s, cx, 172 * s);
    ctx.quadraticCurveTo(cx + 96 * s, 176 * s, cx + 100 * s, h);
    ctx.closePath();
    fillStroke(ctx, css(shirt), 6 * s);
    // head
    ctx.beginPath();
    ctx.ellipse(cx, 110 * s, 62 * s, 68 * s, 0, 0, Math.PI * 2);
    fillStroke(ctx, skin, 6 * s);
    // hair
    ctx.beginPath();
    if (style === 0) {
      ctx.ellipse(cx, 70 * s, 64 * s, 36 * s, 0, Math.PI, Math.PI * 2);
      ctx.closePath();
    } else if (style === 1) {
      ctx.arc(cx, 96 * s, 66 * s, Math.PI * 1.05, Math.PI * 1.95);
      ctx.quadraticCurveTo(cx + 30 * s, 60 * s, cx - 10 * s, 70 * s);
      ctx.closePath();
    } else {
      ctx.arc(cx - 20 * s, 60 * s, 30 * s, 0, Math.PI * 2);
      ctx.arc(cx + 22 * s, 56 * s, 32 * s, 0, Math.PI * 2);
      ctx.arc(cx + 50 * s, 80 * s, 22 * s, 0, Math.PI * 2);
      ctx.arc(cx - 48 * s, 84 * s, 22 * s, 0, Math.PI * 2);
    }
    fillStroke(ctx, hair, 5 * s);
    // eyes
    ctx.fillStyle = '#2b1a14';
    ctx.strokeStyle = '#2b1a14';
    ctx.lineWidth = 6 * s;
    ctx.lineCap = 'round';
    for (const side of [-1, 1]) {
      ctx.beginPath();
      if (pose === 'happy') {
        ctx.arc(cx + side * 22 * s, 116 * s, 10 * s, Math.PI * 1.1, Math.PI * 1.9);
        ctx.stroke();
      } else {
        ctx.ellipse(cx + side * 22 * s, 112 * s, 7 * s, 9 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        if (pose === 'sad') {
          ctx.beginPath();
          ctx.moveTo(cx + side * 32 * s, 92 * s);
          ctx.lineTo(cx + side * 12 * s, 98 * s);
          ctx.stroke();
        }
      }
    }
    ctx.fillStyle = 'rgba(255,120,110,0.45)';
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(cx + side * 40 * s, 136 * s, 11 * s, 6 * s, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.strokeStyle = '#2b1a14';
    if (pose === 'happy') {
      ctx.fillStyle = '#7a2a2a';
      ctx.moveTo(cx - 20 * s, 140 * s);
      ctx.quadraticCurveTo(cx, 168 * s, cx + 20 * s, 140 * s);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    } else if (pose === 'sad') {
      ctx.arc(cx, 160 * s, 14 * s, Math.PI * 1.2, Math.PI * 1.8);
      ctx.stroke();
    } else {
      ctx.arc(cx, 136 * s, 13 * s, Math.PI * 0.2, Math.PI * 0.8);
      ctx.stroke();
    }
  };
}

// ---------------------------------------------------------------- shops

const SHOP_COLORS: Record<string, number> = {
  bagel: 0xff9f1c,
  pizza: 0xff5a5f,
  deli: 0x3bb273,
  flower: 0xff70a6,
  candy: 0x8e6cef,
};

function shopColor(shopKey: string): number {
  return SHOP_COLORS[shopKey] ?? ITEM_COLORS[hash(shopKey) % ITEM_COLORS.length]!;
}

function drawFacade(shopKey: string, locked: boolean): Draw {
  const col = shopColor(shopKey);
  return (ctx, w) => {
    const s = w / 360;
    if (locked) {
      rr(ctx, 20 * s, 40 * s, 320 * s, 320 * s, 18 * s);
      ctx.fillStyle = 'rgba(74,44,42,0.42)';
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(10 * s, 110 * s);
      ctx.lineTo(180 * s, 20 * s);
      ctx.lineTo(350 * s, 110 * s);
      ctx.closePath();
      ctx.fill();
      return;
    }
    // building
    rr(ctx, 24 * s, 60 * s, 312 * s, 300 * s, 14 * s);
    fillStroke(ctx, '#fbe3c4', 7 * s);
    // bricks
    ctx.strokeStyle = 'rgba(160,90,60,0.25)';
    ctx.lineWidth = 3 * s;
    for (let y = 80; y < 350; y += 26) {
      ctx.beginPath();
      ctx.moveTo(30 * s, y * s);
      ctx.lineTo(330 * s, y * s);
      ctx.stroke();
    }
    // sign
    rr(ctx, 60 * s, 18 * s, 240 * s, 64 * s, 16 * s);
    fillStroke(ctx, css(col), 7 * s);
    // awning
    const aw = 300 * s;
    const ax = 30 * s;
    const ay = 104 * s;
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      const x0 = ax + (i * aw) / 6;
      ctx.moveTo(x0, ay);
      ctx.lineTo(x0 + aw / 6, ay);
      ctx.lineTo(x0 + aw / 6, ay + 40 * s);
      ctx.quadraticCurveTo(x0 + aw / 12, ay + 60 * s, x0, ay + 40 * s);
      ctx.closePath();
      ctx.fillStyle = i % 2 ? '#fff' : css(col);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.rect(ax, ay, aw, 40 * s);
    ctx.lineWidth = 6 * s;
    ctx.strokeStyle = INK;
    ctx.stroke();
    // window + door
    rr(ctx, 50 * s, 180 * s, 150 * s, 110 * s, 10 * s);
    fillStroke(ctx, '#bfe9ff', 6 * s);
    gloss(ctx, 90 * s, 210 * s, 30 * s, 12 * s);
    rr(ctx, 224 * s, 180 * s, 84 * s, 180 * s, 10 * s);
    fillStroke(ctx, css(shade2(col, -0.25)), 6 * s);
    ctx.beginPath();
    ctx.arc(292 * s, 276 * s, 7 * s, 0, Math.PI * 2);
    ctx.fillStyle = '#ffd23f';
    ctx.fill();
  };
}

function shade2(hex: number, f: number): number {
  const r = (hex >> 16) & 255;
  const g = (hex >> 8) & 255;
  const b = hex & 255;
  const m = (v: number) => Math.max(0, Math.min(255, Math.round(f >= 0 ? v + (255 - v) * f : v * (1 + f))));
  return (m(r) << 16) | (m(g) << 8) | m(b);
}

function drawShopBase(shopKey: string): Draw {
  const col = shopColor(shopKey);
  return (ctx, w, h) => {
    const s = w / 1024;
    // wall
    const g = ctx.createLinearGradient(0, 0, 0, h * 0.7);
    g.addColorStop(0, css(shade2(col, 0.78)));
    g.addColorStop(1, css(shade2(col, 0.62)));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h * 0.7);
    ctx.fillStyle = css(shade2(col, 0.5));
    for (let x = 0; x < w; x += 64 * s) ctx.fillRect(x, 0, 22 * s, h * 0.7);
    // floor
    ctx.fillStyle = '#c99a6a';
    ctx.fillRect(0, h * 0.7, w, h * 0.3);
    ctx.strokeStyle = 'rgba(90,50,30,0.35)';
    ctx.lineWidth = 4 * s;
    for (let y = h * 0.7; y < h; y += 44 * s) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.fillStyle = INK;
    ctx.fillRect(0, h * 0.7 - 6 * s, w, 12 * s);
    // empty-shop cues: a crate and a broom-less emptiness
    ctx.globalAlpha = 0.25;
    rr(ctx, 380 * s, 420 * s, 260 * s, 110 * s, 10 * s);
    ctx.fillStyle = INK;
    ctx.fill();
    ctx.globalAlpha = 1;
  };
}

/** Upgrade piece art by upgrade kind and style (a/b/c palettes). */
function drawUpgrade(kind: string, style: string, shopKey: string): FallbackSpec {
  const base = shopColor(shopKey);
  const pal = style === 'b' ? [0x3a86ff, 0xbfe9ff] : style === 'c' ? [0x3bb273, 0xd8f5c8] : [base, shade2(base, 0.7)];
  const main = css(pal[0]!);
  const light = css(pal[1]!);
  const specs: Record<string, FallbackSpec> = {
    counter: {
      w: 520,
      h: 220,
      draw: (ctx, w, h) => {
        rr(ctx, 6, 30, w - 12, h - 36, 18);
        fillStroke(ctx, main, 8);
        rr(ctx, 0, 6, w, 44, 14);
        fillStroke(ctx, light, 8);
        for (let i = 0; i < 4; i++) {
          rr(ctx, 30 + i * ((w - 60) / 4), 80, (w - 60) / 4 - 16, h - 110, 12);
          fillStroke(ctx, light, 5);
        }
      },
    },
    sign: {
      w: 480,
      h: 140,
      draw: (ctx, w, h) => {
        rr(ctx, 6, 6, w - 12, h - 12, 28);
        fillStroke(ctx, main, 8);
        rr(ctx, 26, 24, w - 52, h - 48, 18);
        fillStroke(ctx, light, 5);
        starPath(ctx, w / 2, h / 2, 34, 15);
        fillStroke(ctx, '#ffd23f', 5);
      },
    },
    awning: {
      w: 900,
      h: 150,
      draw: (ctx, w, h) => {
        const n = 9;
        for (let i = 0; i < n; i++) {
          ctx.beginPath();
          const x0 = (i * w) / n;
          ctx.moveTo(x0, 0);
          ctx.lineTo(x0 + w / n, 0);
          ctx.lineTo(x0 + w / n, h * 0.7);
          ctx.quadraticCurveTo(x0 + w / n / 2, h, x0, h * 0.7);
          ctx.closePath();
          fillStroke(ctx, i % 2 ? '#fff' : main, 6);
        }
      },
    },
    display: {
      w: 260,
      h: 240,
      draw: (ctx, w, h) => {
        rr(ctx, 6, 60, w - 12, h - 66, 14);
        fillStroke(ctx, main, 7);
        rr(ctx, 20, 6, w - 40, 110, 18);
        fillStroke(ctx, 'rgba(191,233,255,0.85)', 7);
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.arc(60 + i * 70, 84, 22, 0, Math.PI * 2);
          fillStroke(ctx, ['#ffb35c', '#ff8fa3', '#9be564'][i]!, 4);
        }
        gloss(ctx, 70, 34, 30, 10);
      },
    },
    lights: {
      w: 700,
      h: 150,
      draw: (ctx, w) => {
        ctx.strokeStyle = INK;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(0, 20);
        ctx.quadraticCurveTo(w / 2, 90, w, 20);
        ctx.stroke();
        for (let i = 1; i < 8; i++) {
          const x = (i * w) / 8;
          const y = 20 + 70 * (1 - Math.pow((x - w / 2) / (w / 2), 2)) * 0.95;
          ctx.beginPath();
          ctx.ellipse(x, y + 26, 16, 22, 0, 0, Math.PI * 2);
          fillStroke(ctx, i % 2 ? '#ffe066' : light, 4);
          ctx.save();
          ctx.globalAlpha = 0.3;
          ctx.beginPath();
          ctx.arc(x, y + 26, 34, 0, Math.PI * 2);
          ctx.fillStyle = '#fff3a0';
          ctx.fill();
          ctx.restore();
        }
      },
    },
    floor: {
      w: 900,
      h: 160,
      draw: (ctx, w, h) => {
        ctx.beginPath();
        ctx.ellipse(w / 2, h / 2, w / 2 - 8, h / 2 - 8, 0, 0, Math.PI * 2);
        fillStroke(ctx, main, 7);
        ctx.beginPath();
        ctx.ellipse(w / 2, h / 2, w / 2 - 50, h / 2 - 30, 0, 0, Math.PI * 2);
        fillStroke(ctx, light, 5);
      },
    },
    shelves: {
      w: 240,
      h: 300,
      draw: (ctx, w, h) => {
        rr(ctx, 6, 6, w - 12, h - 12, 12);
        fillStroke(ctx, main, 7);
        for (let i = 0; i < 3; i++) {
          const y = 40 + i * 85;
          rr(ctx, 20, y + 46, w - 40, 14, 5);
          fillStroke(ctx, light, 4);
          for (let j = 0; j < 3; j++) {
            rr(ctx, 30 + j * 62, y, 44, 46, 8);
            fillStroke(ctx, ['#ffcf8a', '#ffa8a8', '#b8f0c0'][(i + j) % 3]!, 4);
          }
        }
      },
    },
    plants: {
      w: 180,
      h: 260,
      draw: (ctx, w, h) => {
        rr(ctx, 40, h - 90, w - 80, 84, 14);
        fillStroke(ctx, main, 6);
        const leaf = style === 'c' ? '#2f9e44' : style === 'b' ? '#40c057' : '#69db7c';
        for (let i = 0; i < 5; i++) {
          ctx.beginPath();
          const a = -Math.PI / 2 + (i - 2) * 0.45;
          ctx.ellipse(w / 2 + Math.cos(a) * 50, h - 110 + Math.sin(a) * 70, 22, 50, a + Math.PI / 2, 0, Math.PI * 2);
          fillStroke(ctx, leaf, 5);
        }
      },
    },
    menu: {
      w: 220,
      h: 260,
      draw: (ctx, w, h) => {
        rr(ctx, 6, 6, w - 12, h - 12, 16);
        fillStroke(ctx, '#2d3a3a', 8);
        rr(ctx, 6, 6, w - 12, 50, 16);
        fillStroke(ctx, main, 6);
        ctx.strokeStyle = light;
        ctx.lineWidth = 8;
        ctx.lineCap = 'round';
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(34, 90 + i * 40);
          ctx.lineTo(w - 34 - (i % 2) * 40, 90 + i * 40);
          ctx.stroke();
        }
      },
    },
    door: {
      w: 200,
      h: 380,
      draw: (ctx, w, h) => {
        rr(ctx, 6, 6, w - 12, h - 12, 20);
        fillStroke(ctx, main, 8);
        rr(ctx, 30, 30, w - 60, 150, 14);
        fillStroke(ctx, 'rgba(191,233,255,0.9)', 6);
        ctx.beginPath();
        ctx.arc(w - 40, h / 2 + 30, 10, 0, Math.PI * 2);
        fillStroke(ctx, '#ffd23f', 4);
      },
    },
  };
  return specs[kind] ?? { w: 200, h: 200, draw: (ctx, w, h) => generic(ctx, w, h, kind) };
}

function drawStreet(): Draw {
  return (ctx, w, h) => {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#ffe2b8');
    g.addColorStop(0.55, '#ffd0a0');
    g.addColorStop(1, '#ffc38a');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // skyline
    ctx.fillStyle = 'rgba(214,140,110,0.35)';
    let x = 0;
    let k = 7;
    while (x < w) {
      k = (k * 1103515245 + 12345) & 0x7fffffff;
      const bw = 80 + (k % 120);
      const bh = 180 + ((k >>> 8) % 260);
      ctx.fillRect(x, h * 0.62 - bh, bw, bh);
      x += bw + 10;
    }
    ctx.fillStyle = 'rgba(214,140,110,0.5)';
    x = 40;
    while (x < w) {
      k = (k * 1103515245 + 12345) & 0x7fffffff;
      const bw = 60 + (k % 100);
      const bh = 100 + ((k >>> 8) % 160);
      ctx.fillRect(x, h * 0.62 - bh, bw, bh);
      x += bw + 60;
    }
    // sidewalk + road
    ctx.fillStyle = '#e8c9a4';
    ctx.fillRect(0, h * 0.62, w, h * 0.14);
    ctx.fillStyle = '#b8a597';
    ctx.fillRect(0, h * 0.76, w, h * 0.24);
    ctx.fillStyle = '#fff8e6';
    for (let i = 0; i < w; i += 160) ctx.fillRect(i + 30, h * 0.87, 90, 12);
    ctx.fillStyle = INK;
    ctx.fillRect(0, h * 0.755, w, 8);
  };
}

// ---------------------------------------------------------------- icons & ui

function drawCoin(ctx: Ctx, w: number): void {
  const c = w / 2;
  ctx.beginPath();
  ctx.arc(c, c + w * 0.03, w * 0.42, 0, Math.PI * 2);
  ctx.fillStyle = '#c98a00';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(c, c, w * 0.42, 0, Math.PI * 2);
  fillStroke(ctx, '#ffcf3f', w * 0.06);
  ctx.beginPath();
  ctx.arc(c, c, w * 0.29, 0, Math.PI * 2);
  ctx.strokeStyle = '#e0a800';
  ctx.lineWidth = w * 0.05;
  ctx.stroke();
  starPath(ctx, c, c, w * 0.17, w * 0.08);
  ctx.fillStyle = '#fff2b0';
  ctx.fill();
}

function drawGem(ctx: Ctx, w: number): void {
  ctx.beginPath();
  ctx.moveTo(w * 0.25, w * 0.2);
  ctx.lineTo(w * 0.75, w * 0.2);
  ctx.lineTo(w * 0.92, w * 0.4);
  ctx.lineTo(w * 0.5, w * 0.88);
  ctx.lineTo(w * 0.08, w * 0.4);
  ctx.closePath();
  fillStroke(ctx, '#4cc9f0', w * 0.06);
  ctx.beginPath();
  ctx.moveTo(w * 0.08, w * 0.4);
  ctx.lineTo(w * 0.92, w * 0.4);
  ctx.moveTo(w * 0.36, w * 0.2);
  ctx.lineTo(w * 0.5, w * 0.88);
  ctx.lineTo(w * 0.64, w * 0.2);
  ctx.strokeStyle = 'rgba(29,53,87,0.45)';
  ctx.lineWidth = w * 0.03;
  ctx.stroke();
  gloss(ctx, w * 0.35, w * 0.3, w * 0.1, w * 0.05);
}

function drawStar(ctx: Ctx, w: number, full: boolean): void {
  starPath(ctx, w / 2, w * 0.53, w * 0.44, w * 0.2);
  fillStroke(ctx, full ? '#ffc400' : 'rgba(74,44,42,0.25)', w * 0.06, full ? INK : 'rgba(74,44,42,0.45)');
  if (full) gloss(ctx, w * 0.4, w * 0.38, w * 0.08, w * 0.04);
}

function drawBooster(kind: string): Draw {
  return (ctx, w) => {
    const s = w / 128;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    switch (kind) {
      case 'undo':
        ctx.beginPath();
        ctx.arc(66 * s, 70 * s, 34 * s, Math.PI * 1.15, Math.PI * 0.75);
        ctx.strokeStyle = INK;
        ctx.lineWidth = 22 * s;
        ctx.stroke();
        ctx.strokeStyle = '#3a86ff';
        ctx.lineWidth = 12 * s;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(14 * s, 36 * s);
        ctx.lineTo(40 * s, 62 * s);
        ctx.lineTo(50 * s, 22 * s);
        ctx.closePath();
        fillStroke(ctx, '#3a86ff', 6 * s);
        break;
      case 'shuffle':
        for (const [a, b, col] of [
          [22, 96, '#ff9f1c'],
          [96, 22, '#8e6cef'],
        ] as const) {
          ctx.beginPath();
          ctx.moveTo(18 * s, a * s);
          ctx.bezierCurveTo(64 * s, a * s, 64 * s, b * s, 100 * s, b * s);
          ctx.strokeStyle = INK;
          ctx.lineWidth = 20 * s;
          ctx.stroke();
          ctx.strokeStyle = col;
          ctx.lineWidth = 10 * s;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(96 * s, (b - 16) * s);
          ctx.lineTo(118 * s, b * s);
          ctx.lineTo(96 * s, (b + 16) * s);
          ctx.closePath();
          fillStroke(ctx, col, 5 * s);
        }
        break;
      case 'magnet':
        ctx.beginPath();
        ctx.arc(64 * s, 60 * s, 34 * s, Math.PI, 0);
        ctx.strokeStyle = INK;
        ctx.lineWidth = 34 * s;
        ctx.stroke();
        ctx.strokeStyle = '#ff5a5f';
        ctx.lineWidth = 22 * s;
        ctx.stroke();
        for (const x of [30, 98]) {
          ctx.beginPath();
          ctx.rect((x - 11) * s, 58 * s, 22 * s, 42 * s);
          fillStroke(ctx, x === 30 ? '#ff5a5f' : '#ff5a5f', 6 * s);
          ctx.beginPath();
          ctx.rect((x - 11) * s, 86 * s, 22 * s, 18 * s);
          fillStroke(ctx, '#dfe7ef', 6 * s);
        }
        break;
      case 'extraslot':
        rr(ctx, 16 * s, 34 * s, 96 * s, 72 * s, 18 * s);
        fillStroke(ctx, '#e9b57a', 7 * s);
        ctx.beginPath();
        ctx.arc(94 * s, 36 * s, 24 * s, 0, Math.PI * 2);
        fillStroke(ctx, '#3bb273', 6 * s);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 8 * s;
        ctx.beginPath();
        ctx.moveTo(82 * s, 36 * s);
        ctx.lineTo(106 * s, 36 * s);
        ctx.moveTo(94 * s, 24 * s);
        ctx.lineTo(94 * s, 48 * s);
        ctx.stroke();
        break;
    }
  };
}

function generic(ctx: Ctx, w: number, h: number, text: string): void {
  rr(ctx, 4, 4, w - 8, h - 8, Math.min(w, h) * 0.2);
  fillStroke(ctx, '#ffe3b0', 5);
  label(ctx, text.slice(0, 10), w / 2, h / 2, Math.min(w, h) * 0.16, INK, null);
}

// ---------------------------------------------------------------- registry

/** Resolve an asset id to a fallback drawing. Unknown ids get a labelled placeholder. */
export function fallbackFor(key: string): FallbackSpec {
  let m: RegExpExecArray | null;
  if ((m = /^itm_(.+?)(_cb)?$/.exec(key))) {
    const id = `itm_${m[1]}`;
    return { w: 192, h: 192, draw: drawItem(id, !!m[2]) };
  }
  if ((m = /^chr_pip_act_([a-z]+)_(\d+)$/.exec(key))) {
    const frames = PIP_FRAMES[m[1]!] ?? PIP_FRAMES.idle!;
    return { w: 256, h: 256, draw: drawPip(frames[Number(m[2]) % frames.length]!) };
  }
  if (key === 'chr_pip_bag_body') return { w: 640, h: 180, draw: drawBagBody() };
  if ((m = /^cus_(.+)_(neutral|happy|sad)$/.exec(key))) return { w: 256, h: 256, draw: drawCustomer(m[1]!, m[2]!) };
  if ((m = /^shp_[a-z]+_([a-z]+)_facade(_locked)?$/.exec(key))) return { w: 360, h: 360, draw: drawFacade(m[1]!, !!m[2]) };
  if ((m = /^shp_[a-z]+_([a-z]+)_base$/.exec(key))) return { w: 1024, h: 640, draw: drawShopBase(m[1]!) };
  if ((m = /^shp_[a-z]+_([a-z]+)_upg(\d\d)_([abc])$/.exec(key))) {
    const kinds = ['counter', 'sign', 'awning', 'display', 'lights', 'floor', 'shelves', 'plants', 'menu', 'door'];
    return drawUpgrade(kinds[Number(m[2]) - 1] ?? 'counter', m[3]!, m[1]!);
  }
  if (/^env_.+_street_/.test(key)) return { w: 2048, h: 1024, draw: drawStreet() };
  switch (key) {
    case 'ico_cur_coin':
      return { w: 128, h: 128, draw: (ctx, w) => drawCoin(ctx, w) };
    case 'ico_cur_gem':
      return { w: 128, h: 128, draw: (ctx, w) => drawGem(ctx, w) };
    case 'ico_cur_star':
    case 'ui_star_full':
      return { w: 128, h: 128, draw: (ctx, w) => drawStar(ctx, w, true) };
    case 'ui_star_empty':
      return { w: 128, h: 128, draw: (ctx, w) => drawStar(ctx, w, false) };
    case 'ico_boost_undo':
    case 'ico_boost_shuffle':
    case 'ico_boost_magnet':
    case 'ico_boost_extraslot':
      return { w: 128, h: 128, draw: drawBooster(key.slice('ico_boost_'.length)) };
    case 'ui_play_slot':
      return {
        w: 128,
        h: 128,
        draw: (ctx, w, h) => {
          rr(ctx, 8, 8, w - 16, h - 16, 26);
          ctx.fillStyle = 'rgba(90,46,31,0.28)';
          ctx.fill();
          rr(ctx, 8, 14, w - 16, h - 22, 24);
          ctx.fillStyle = 'rgba(255,255,255,0.35)';
          ctx.fill();
        },
      };
    case 'ui_check':
      return {
        w: 96,
        h: 96,
        draw: (ctx, w) => {
          ctx.beginPath();
          ctx.arc(w / 2, w / 2, w * 0.44, 0, Math.PI * 2);
          fillStroke(ctx, '#3bb273', w * 0.06);
          ctx.beginPath();
          ctx.moveTo(w * 0.28, w * 0.52);
          ctx.lineTo(w * 0.44, w * 0.68);
          ctx.lineTo(w * 0.73, w * 0.36);
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = w * 0.11;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        },
      };
    case 'ui_lock':
      return {
        w: 96,
        h: 96,
        draw: (ctx, w) => {
          ctx.beginPath();
          ctx.arc(w / 2, w * 0.42, w * 0.2, Math.PI, 0);
          ctx.strokeStyle = INK;
          ctx.lineWidth = w * 0.1;
          ctx.stroke();
          rr(ctx, w * 0.2, w * 0.4, w * 0.6, w * 0.46, w * 0.1);
          fillStroke(ctx, '#ffc845', w * 0.06);
        },
      };
    case 'ui_map_node':
    case 'ui_map_node_current':
    case 'ui_map_node_locked':
      return {
        w: 128,
        h: 128,
        draw: (ctx, w) => {
          const col = key.endsWith('current') ? '#ff6b5b' : key.endsWith('locked') ? '#cdb8a3' : '#2ec4b6';
          ctx.beginPath();
          ctx.arc(w / 2, w / 2 + 6, w * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(0,0,0,0.2)';
          ctx.fill();
          ctx.beginPath();
          ctx.arc(w / 2, w / 2, w * 0.4, 0, Math.PI * 2);
          fillStroke(ctx, col, w * 0.06);
          gloss(ctx, w * 0.38, w * 0.32, w * 0.12, w * 0.06);
        },
      };
    case 'prp_mech_wrap':
      return {
        w: 192,
        h: 192,
        draw: (ctx, w, h) => {
          rr(ctx, 14, 14, w - 28, h - 28, 30);
          fillStroke(ctx, '#ff8fab', 8);
          ctx.fillStyle = '#ffd6e0';
          ctx.fillRect(w / 2 - 14, 18, 28, h - 36);
          ctx.fillRect(18, h / 2 - 14, w - 36, 28);
          for (const side of [-1, 1]) {
            ctx.beginPath();
            ctx.ellipse(w / 2 + side * 26, h / 2 - 6, 26, 16, side * 0.5, 0, Math.PI * 2);
            fillStroke(ctx, '#ff5d8f', 6);
          }
          label(ctx, '?', w / 2, h / 2 + 34, 44, '#fff', INK);
        },
      };
    case 'prp_mech_crate_1':
    case 'prp_mech_crate_2':
    case 'prp_mech_crate_3':
      return {
        w: 192,
        h: 192,
        draw: (ctx, w, h) => {
          const hp = Number(key.slice(-1));
          rr(ctx, 10, 10, w - 20, h - 20, 18);
          fillStroke(ctx, '#c98a4b', 8);
          ctx.strokeStyle = '#8a5a36';
          ctx.lineWidth = 10;
          ctx.beginPath();
          ctx.moveTo(24, 24);
          ctx.lineTo(w - 24, h - 24);
          if (hp >= 2) {
            ctx.moveTo(w - 24, 24);
            ctx.lineTo(24, h - 24);
          }
          ctx.stroke();
          ctx.strokeStyle = INK;
          ctx.lineWidth = 6;
          for (let i = 0; i < 3 - hp; i++) {
            ctx.beginPath();
            ctx.moveTo(40 + i * 50, 30);
            ctx.lineTo(60 + i * 50, 80);
            ctx.lineTo(44 + i * 50, 120);
            ctx.stroke();
          }
          label(ctx, String(hp), w - 38, 40, 40, '#fff', INK);
        },
      };
    case 'prp_mech_ice_1':
    case 'prp_mech_ice_2':
      return {
        w: 192,
        h: 192,
        draw: (ctx, w, h) => {
          const n = Number(key.slice(-1));
          rr(ctx, 8, 8, w - 16, h - 16, 30);
          ctx.fillStyle = n === 2 ? 'rgba(170,225,255,0.72)' : 'rgba(190,235,255,0.55)';
          ctx.fill();
          ctx.lineWidth = 6;
          ctx.strokeStyle = 'rgba(255,255,255,0.95)';
          ctx.stroke();
          ctx.strokeStyle = 'rgba(40,120,170,0.8)';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.moveTo(40, 30);
          ctx.lineTo(80, 90);
          ctx.lineTo(60, 150);
          if (n === 1) {
            ctx.moveTo(80, 90);
            ctx.lineTo(150, 110);
            ctx.moveTo(120, 30);
            ctx.lineTo(100, 70);
          }
          ctx.stroke();
          gloss(ctx, 60, 50, 30, 12);
        },
      };
    case 'prp_mech_conveyor':
      return {
        w: 512,
        h: 128,
        draw: (ctx, w, h) => {
          rr(ctx, 4, 16, w - 8, h - 32, 40);
          fillStroke(ctx, '#6c757d', 8);
          ctx.strokeStyle = '#adb5bd';
          ctx.lineWidth = 8;
          for (let x = 40; x < w - 20; x += 48) {
            ctx.beginPath();
            ctx.moveTo(x, 30);
            ctx.lineTo(x - 16, h - 30);
            ctx.stroke();
          }
        },
      };
    case 'vfx_sparkle':
      return {
        w: 64,
        h: 64,
        draw: (ctx, w) => {
          starPath(ctx, w / 2, w / 2, w * 0.46, w * 0.12, 4);
          ctx.fillStyle = '#fff';
          ctx.fill();
        },
      };
    case 'fx_confetti':
      return {
        w: 20,
        h: 32,
        draw: (ctx, w, h) => {
          rr(ctx, 1, 1, w - 2, h - 2, 4);
          ctx.fillStyle = '#fff';
          ctx.fill();
        },
      };
    case 'ui_play_tile':
      return {
        w: 192,
        h: 204,
        draw: (ctx, w) => {
          // depth edge
          rr(ctx, 6, 18, w - 12, w - 12, 34);
          ctx.fillStyle = '#c9a27a';
          ctx.fill();
          ctx.lineWidth = 6;
          ctx.strokeStyle = INK;
          ctx.stroke();
          // top face
          rr(ctx, 6, 6, w - 12, w - 12, 34);
          const g = ctx.createLinearGradient(0, 6, 0, w - 6);
          g.addColorStop(0, '#fffdf7');
          g.addColorStop(1, '#f7e8cf');
          ctx.fillStyle = g;
          ctx.fill();
          ctx.stroke();
          rr(ctx, 18, 16, w - 36, 22, 11);
          ctx.fillStyle = 'rgba(255,255,255,0.8)';
          ctx.fill();
        },
      };
    case 'vfx_dot':
      return {
        w: 32,
        h: 32,
        draw: (ctx, w) => {
          ctx.beginPath();
          ctx.arc(w / 2, w / 2, w * 0.45, 0, Math.PI * 2);
          ctx.fillStyle = '#fff';
          ctx.fill();
        },
      };
  }
  if ((m = /^vfx_confetti_(.+)$/.exec(key))) {
    const k = hash(m[1]!);
    const col = css(ITEM_COLORS[k % ITEM_COLORS.length]!);
    return {
      w: 24,
      h: 36,
      draw: (ctx, w, h) => {
        rr(ctx, 2, 2, w - 4, h - 4, 5);
        ctx.fillStyle = col;
        ctx.fill();
      },
    };
  }
  return { w: 128, h: 128, draw: (ctx, w, h) => generic(ctx, w, h, key.split('_').slice(-2).join(' ')) };
}

/** Render a fallback into a fresh canvas at `scale` × nominal size. */
export function renderFallback(key: string, scale = 1): HTMLCanvasElement {
  const spec = fallbackFor(key);
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(spec.w * scale);
  canvas.height = Math.ceil(spec.h * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  spec.draw(ctx, spec.w, spec.h);
  return canvas;
}
